import { Link } from "@tanstack/react-router";
import { ShieldCheck } from "lucide-react";

import {
  CONTENT_REVIEWED_ON,
  CONTENT_REVIEWER,
  SOURCING_LINE,
  formatReviewDate,
  type ContentSource,
} from "@/lib/content-review";

type Props = {
  /** ISO date, defaults to the library-wide review date. */
  reviewedOn?: string;
  /** Page-specific sources shown above the sitewide sourcing note. */
  sources?: ContentSource[];
  className?: string;
};

/**
 * Shows when a page was last reviewed, how it is sourced, and which
 * primary sources back it up. Rendered at the foot of editorial pages.
 */
export function ContentReviewNote({ reviewedOn = CONTENT_REVIEWED_ON, sources, className }: Props) {
  return (
    <footer
      className={`rounded-xl border border-border bg-muted/30 p-6 ${className ?? ""}`}
      aria-labelledby="content-review-heading"
    >
      <h2 id="content-review-heading" className="flex items-center gap-2 font-display text-lg">
        <ShieldCheck className="size-4 text-brass" aria-hidden="true" />
        How this page is sourced
      </h2>
      <p className="mt-3 text-sm text-muted-foreground">
        Last reviewed {formatReviewDate(reviewedOn)} by {CONTENT_REVIEWER}. {SOURCING_LINE}
      </p>

      {sources && sources.length > 0 ? (
        <ul className="mt-4 space-y-2 text-sm">
          {sources.map((source) => (
            <li key={source.url}>
              <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium underline underline-offset-4"
              >
                {source.label}
              </a>
              {source.note ? (
                <span className="text-muted-foreground">: {source.note}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      <p className="mt-4 text-sm text-muted-foreground">
        For general reference only. Not legal advice, and reading it creates no attorney-client relationship.{" "}
        <Link to="/editorial-standards" className="underline underline-offset-4">
          Read our editorial standards
        </Link>{" "}
        or{" "}
        <Link to="/contact/support" className="underline underline-offset-4">
          report a correction
        </Link>
        .
      </p>
    </footer>
  );
}
